import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * PWAInstallBanner — Mobile V1.
 * Muestra un aviso discreto invitando al usuario a instalar la app en su
 * pantalla de inicio. En Android usa el evento `beforeinstallprompt`. En iOS
 * (Safari), instruye manualmente ya que iOS no expone la API.
 */
export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed]           = useState(false);
  const [installed, setInstalled]           = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Detect if already installed
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }
    if (localStorage.getItem("rf_pwa_dismissed_at")) {
      const dt = Number(localStorage.getItem("rf_pwa_dismissed_at"));
      // Show again after 7 days
      if (Date.now() - dt < 7 * 24 * 3600 * 1000) { setDismissed(true); return; }
    }
    // Detect iOS Safari
    const ua = window.navigator.userAgent || "";
    const ios = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    setIsIOS(ios);
    if (ios) { setShowIosHint(true); return; }
    // Android/Chromium prompt
    const onBIP = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInst = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInst);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInst);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* noop */ }
    setDeferredPrompt(null);
    dismiss();
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("rf_pwa_dismissed_at", String(Date.now())); } catch {}
  };

  if (installed || dismissed) return null;
  if (!deferredPrompt && !showIosHint) return null;

  return (
    <div
      className="md:hidden fixed left-3 right-3 bottom-20 z-40 rounded-2xl bg-gradient-to-br from-[#C8A24A]/95 to-[#B88C2D]/95 text-[#0A0A0A] shadow-2xl p-4 flex items-start gap-3"
      data-testid="pwa-install-banner"
      role="alert"
    >
      <img src="/logo.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold">Instala Rocafuerte</p>
        {isIOS ? (
          <p className="text-xs mt-0.5 leading-snug">
            Toca <span className="font-bold">Compartir</span> y luego <span className="font-bold">"Agregar a inicio"</span> para instalarla.
          </p>
        ) : (
          <>
            <p className="text-xs mt-0.5 leading-snug">
              Añádela a tu pantalla de inicio para acceso rápido y avisos.
            </p>
            <button
              onClick={install}
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold bg-[#0A0A0A] text-[#C8A24A] px-3 py-1.5 rounded-full"
              data-testid="pwa-install-btn"
            >
              <DownloadSimple size={12} weight="bold" /> Instalar
            </button>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        className="p-1 -m-1 flex-shrink-0"
        aria-label="Cerrar aviso"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
